/**
 * Client-accessible environment config.
 *
 * Company-specific values (theme, company name, URLs) are injected at container
 * start by docker-entrypoint.sh into window.__CONFIG__ (runtime config).
 * import.meta.env values are used as fallback for local development.
 */

import packageJson from '../../package.json'

interface RuntimeConfig {
  theme?: string
  companyName?: string
  companyLabel?: string
  supportEmail?: string
  androidAppUrl?: string
  iosAppUrl?: string
  signalrUrl?: string
  appUrl?: string
  assetsBaseUrl?: string
}

declare global {
  interface Window {
    __CONFIG__?: RuntimeConfig
  }
}

const rc: RuntimeConfig = window.__CONFIG__ ?? {}

export const config = {
  /** Base URL for client-side API requests.
   *  Uso de `||` (no `??`) para tratar string vacio como "missing" — el
   *  pipeline de QA escribe `.env` con `VITE_API_BASE_URL=` vacio si el
   *  secret no esta configurado, y `?? fallback` no triggerea con ''. */
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api-be/',

  /** Canonical public URL of this app */
  appUrl: rc.appUrl || import.meta.env.VITE_APP_URL || 'http://localhost:3001',

  /** SignalR hub endpoint */
  signalrUrl: rc.signalrUrl || import.meta.env.VITE_SIGNALR_URL || '',

  /**
   * Brand / theme key (e.g. 'billeteraPais').
   * Display label is derived via companyLabel below.
   */
  theme: rc.theme || import.meta.env.VITE_THEME || 'default',

  /** Company name key — used for theme / feature flags. */
  companyName: rc.companyName || import.meta.env.VITE_COMPANY_NAME || 'PSP',

  /** Human-readable company label shown in the UI. */
  companyLabel: rc.companyLabel
    || (import.meta.env.VITE_COMPANY_LABEL as string | undefined)
    || resolveCompanyLabel(rc.companyName || import.meta.env.VITE_COMPANY_NAME || ''),

  /** Support / contact e-mail shown in error messages */
  supportEmail: rc.supportEmail || import.meta.env.VITE_SUPPORT_EMAIL || '',

  /** Mock mode — bypasses real API calls when true */
  isMock: import.meta.env.VITE_MOCK === 'true',

  /** App Store link for the iOS native app (mobile login download CTA). */
  iosAppUrl: rc.iosAppUrl || import.meta.env.VITE_IOS_APP_URL || '',

  /** Google Play link for the Android native app (mobile login download CTA). */
  androidAppUrl: rc.androidAppUrl || import.meta.env.VITE_ANDROID_APP_URL || '',

  /** Base URL for company assets (logos, icons). */
  assetsBaseUrl: rc.assetsBaseUrl || '/public',

  /** App version, sourced from package.json. Shown in the mobile login footer. */
  version: packageJson.version as string,
} as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveCompanyLabel(key: string): string {
  const labels: Record<string, string> = {
    billeteraPais: 'Billetera País',
    masPay:        'MasPay',
  }
  return labels[key] ?? key
}
